import React, { useEffect, useMemo } from 'react';
import { Row, Col, Typography, Statistic, Card, Button, Tag } from 'antd';
import { RollbackOutlined } from '@ant-design/icons';
import { useStoreActions, useStoreState } from '../../store';
import { SimulationCard } from './SimulationCard';
import type { SimulationDetail } from '../../store/simulation/types';
import { SimulationShipmentBatchTable } from './SimulationShipmentsTable';
import { palette } from '@shipium-corp/shipium-ui-theme';
import { useSearchParams } from 'react-router-dom-v5-compat';
import { SimulationRawResultsTable } from './SimulationRawResultsTable';
import type { ShipiumTagConfig } from '../../components/shared/ShipiumTag';
import { ShipiumTagWithConfig } from '../../components/shared/ShipiumTag';

export interface SimulationTabProps {
  simulation: SimulationDetail;
}

const { Text, Title } = Typography;

export const SimulationShipmentsTab = ({ simulation }: SimulationTabProps) => {
  const fetchSimulationRawResults = useStoreActions((actions) => actions.simulation.fetchSimulationRawResults);
  const simulationRawResult = useStoreState((state) => state.simulation.simulationRawResult);
  const simulationId = simulation.simulationId;
  const isArchived = simulation.simulationStatus === 'archived';
  const simulationShipmentBatches = simulation.simulationShipmentBatches || [];
  const totalRowsSum = simulationShipmentBatches.reduce((sum, batch) => sum + batch.totalRows, 0);
  const skippedRowsSum = simulationShipmentBatches.reduce((sum, batch) => sum + batch.skippedRows, 0);
  const simulatedRowsSum =
    simulationShipmentBatches.reduce((sum, batch) => sum + batch.successRows, 0) - skippedRowsSum;
  const errorRowsSum = simulationShipmentBatches.reduce((sum, batch) => sum + batch.errorRows, 0);
  const [query, setQuery] = useSearchParams();

  const batchStatus = query.get('batch-status');
  const setBatchStatus = (status: string | undefined) => {
    if (status) {
      query.set('batch-status', status);
    } else {
      query.delete('batch-status');
    }
    setQuery(query);
  };

  const queryParams = useMemo(() => {
    return {
      date: query.get('date'),
      hour: query.get('hour'),
      status: query.get('status'),
      page: query.get('page'),
      batch: query.get('batch'),
    };
  }, [query]);

  useEffect(() => {
    if (queryParams.date && queryParams.hour && queryParams.status && queryParams.page) {
      fetchSimulationRawResults({
        simulationId,
        date: queryParams.date,
        hour: queryParams.hour,
        status: queryParams.status,
        page: queryParams.page,
      });
    }
  }, [fetchSimulationRawResults, queryParams, simulationId]);

  const showDetails = queryParams.date && queryParams.hour && queryParams.status;
  const selectedBatch = simulationShipmentBatches.find((batch) => batch.simulationBatchId === queryParams.batch);

  const clearQuery = () => {
    query.delete('date');
    query.delete('hour');
    query.delete('status');
    query.delete('page');
    query.delete('batch');
    setQuery(query);
  };

  const exitDetailButton = () => {
    return (
      <Button type={'default'} onClick={clearQuery}>
        <RollbackOutlined />
        Return to Batch Summary
      </Button>
    );
  };

  const batchFilters = () => {
    const batchSummary = simulationShipmentBatches.reduce<Record<string, number>>((summary, batch) => {
      summary[batch.batchStatus] = (summary[batch.batchStatus] || 0) + 1;
      return summary;
    }, {});
    const batchCount = simulation.simulationShipmentBatches?.length || 0;
    const pendingCount = batchSummary['pending'] || 0;
    const processingBatchCount = batchSummary['processing'] || 0;
    const completedBatchCount = batchSummary['completed'] || 0;
    const cancelledBatchCount = batchSummary['cancelled'] || 0;

    const shipiumTagMapStatus: Record<string, ShipiumTagConfig> = {
      all: { color: 'Fuschia', label: `${batchCount} Total Batches`, dark: true, icon: 'config' },
      pending: { color: 'Grey', label: `${pendingCount} Pending`, dark: true, icon: 'wait' },
      processing: { color: 'Blue', label: `${processingBatchCount} Processing`, dark: true, icon: 'processing' },
      completed: { color: 'Teal', label: `${completedBatchCount} Complete`, dark: true, icon: 'check' },
      failed: { color: 'Coral', label: `${cancelledBatchCount} Exception`, dark: true, icon: 'exclamation' },
      cancelled: { color: 'Gold', label: `${cancelledBatchCount} Cancelled`, dark: true, icon: 'warning' },
    };

    return (
      <>
        <Button style={{ padding: 0 }} type="link" onClick={() => setBatchStatus(undefined)}>
          <ShipiumTagWithConfig
            style={!batchStatus ? { borderWidth: '5px' } : undefined}
            configMap={shipiumTagMapStatus}
            tag={'all'}
          />
        </Button>
        <Button style={{ padding: 0 }} type="link" onClick={() => setBatchStatus('pending')}>
          <ShipiumTagWithConfig
            style={batchStatus === 'pending' ? { borderWidth: '5px' } : undefined}
            configMap={shipiumTagMapStatus}
            tag={'pending'}
          />
        </Button>
        <Button style={{ padding: 0 }} type="link" onClick={() => setBatchStatus('processing')}>
          <ShipiumTagWithConfig
            style={batchStatus === 'processing' ? { borderWidth: '5px' } : undefined}
            configMap={shipiumTagMapStatus}
            tag={'processing'}
          />
        </Button>
        <Button style={{ padding: 0 }} type="link" onClick={() => setBatchStatus('completed')}>
          <ShipiumTagWithConfig
            style={batchStatus === 'completed' ? { borderWidth: '5px' } : undefined}
            configMap={shipiumTagMapStatus}
            tag={'completed'}
          />
        </Button>
        <Button style={{ padding: 0 }} type="link" onClick={() => setBatchStatus('cancelled')}>
          <ShipiumTagWithConfig
            style={batchStatus === 'cancelled' ? { borderWidth: '5px' } : undefined}
            configMap={shipiumTagMapStatus}
            tag={'cancelled'}
          />
        </Button>
      </>
    );
  };

  const totalExtra =
    (simulation?.cancelledShipmentCount || 0) > 0 ? (
      <small>({simulation.cancelledShipmentCount?.toLocaleString()} cancelled)</small>
    ) : null;

  return (
    <>
      <Row gutter={32}>
        <Col span={6}>
          <Card bordered={true} style={{ height: 215, background: '#fafafa' }}>
            <Statistic
              title={<Title level={4}>Total Shipments</Title>}
              value={totalRowsSum}
              suffix={totalExtra}
              precision={0}
              valueStyle={{ color: palette.Grey.darkest }}
            />
            <Text type="secondary">
              Total shipments within the simulation date range. Defined by the uploaded shipment set or historical date
              range.
            </Text>
          </Card>
        </Col>
        <Col span={6}>
          <Card bordered={true} style={{ height: 215, background: '#fafafa' }}>
            <Statistic
              title={<Title level={4}>Simulated Shipments</Title>}
              value={simulatedRowsSum}
              precision={0}
              valueStyle={{ color: palette.Teal.dark }}
            />
            <Text type="secondary">
              Shipments which have been executed as part of the simulation. Excludes unmapped shipments and errors.
            </Text>
          </Card>
        </Col>
        <Col span={6}>
          <Card bordered={true} style={{ height: 215, background: '#fafafa' }}>
            <Statistic
              title={<Title level={4}>Skipped Shipments</Title>}
              value={skippedRowsSum}
              precision={0}
              valueStyle={{ color: palette.Gold.dark }}
            />
            <Text type="secondary">
              Shipments which do not have a fulfillment context mapping defined for the simulation.
            </Text>
          </Card>
        </Col>
        <Col span={6}>
          <Card bordered={true} style={{ height: 215, background: '#fafafa' }}>
            <Statistic
              title={<Title level={4}>Errors</Title>}
              value={errorRowsSum}
              precision={0}
              valueStyle={{ color: palette.Coral.dark }}
            />
            <Text type="secondary">
              Shipments for which simulation was attempted but failed due to invalid request parameters or a service
              error.
            </Text>
          </Card>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>&nbsp;</Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          {!showDetails && (
            <SimulationCard
              heading={'Simulation Shipment Batches'}
              subHeading={
                'Simulation Shipments are the individual shipments which will be evaluated by the Shiptime API.'
              }
              archived={isArchived}
              bodyStyle={{ padding: 0, borderTop: '1px solid #eee' }}
              extra={batchFilters()}
            >
              <SimulationShipmentBatchTable simulation={simulation} />
            </SimulationCard>
          )}
          {showDetails && (
            <SimulationCard
              heading={`Simulation Shipment Batch Detail`}
              headingOnClick={() => clearQuery()}
              extra={exitDetailButton()}
              headingCount={selectedBatch?.totalRows}
              subHeading={
                <>
                  <Tag>{queryParams.date}</Tag>
                  <Tag>{queryParams.hour}:00</Tag>
                  <Tag>{queryParams.status}</Tag>
                </>
              }
              archived={isArchived}
              bodyStyle={{ padding: 0, borderTop: '1px solid #eee' }}
            >
              <SimulationRawResultsTable simulationRawResult={simulationRawResult} />
            </SimulationCard>
          )}
        </Col>
      </Row>
    </>
  );
};
